import { styled } from '@mui/material/styles';
import classNames from 'classnames';
import {
  AtomicBlockUtils,
  CompositeDecorator,
  convertFromRaw,
  convertToRaw,
  DefaultDraftBlockRenderMap,
  Editor,
  EditorState,
  getDefaultKeyBinding,
  KeyBindingUtil,
  Modifier,
  RichUtils,
  SelectionState,
} from 'draft-js';
import 'draft-js/dist/Draft.css';
import { Map, OrderedSet } from 'immutable';
import memoize from 'memoize-one';
import { createRef, forwardRef, PureComponent } from 'react';

import { capitalize, debounce, isDeepEqual } from 'my-core/my-utils';
import { assignForwardedRef } from 'my-core/react-utils';

import AttachmentUploadManager from './AttachmentUploadManager';
import { defaultStyleMap, TEXT_COLORS } from './constants';
import Callout from './plugins/callout';
import EquationBlock from './plugins/equation/EquationBlock';
import ImageBlock from './plugins/image/ImageBlock';
import PageBreakBlock from './plugins/PageBreakBlock';
import TableBlock from './plugins/table';
import VideoBlock from './plugins/video/VideoBlock';
import { pluginsConfig } from './pluginsConfig';
import Toolbar from './toolbar/Toolbar';
import { addEquation, copySelection, pasteContent, toggleInlineStyle } from './utils';

const PREFIX = 'Editor';

const classes = {
  wrapper: `${PREFIX}-wrapper`,
  wrapper_readOnly: `${PREFIX}-wrapper_readOnly`,
  wrapper_hidePlaceholder: `${PREFIX}-wrapper_hidePlaceholder`,
  wrapper_fitContent: `${PREFIX}-wrapper_fitContent`,
  wrapper_continuousLists: `${PREFIX}-wrapper_continuousLists`,
  alignLeft: `${PREFIX}-alignLeft`,
  alignCenter: `${PREFIX}-alignCenter`,
  alignRight: `${PREFIX}-alignRight`,
  alignJustify: `${PREFIX}-alignJustify`,
  float: `${PREFIX}-float`,
  floatRight: `${PREFIX}-floatRight`,
  floatLeft: `${PREFIX}-floatLeft`,
  solutionBlock: `${PREFIX}-solutionBlock`,
  solutionBlockFirst: `${PREFIX}-solutionBlockFirst`,
  solutionBlockLast: `${PREFIX}-solutionBlockLast`,
  solutionBlockHidden: `${PREFIX}-solutionBlockHidden`,
  line_spacing_1: `${PREFIX}-line_spacing_1`,
  line_spacing_1_5: `${PREFIX}-line_spacing_1_5`,
  line_spacing_2: `${PREFIX}-line_spacing_2`,
  line_spacing_3: `${PREFIX}-line_spacing_3`,
  margin_top_sm: `${PREFIX}-margin_top_sm`,
  margin_top_md: `${PREFIX}-margin_top_md`,
  margin_top_lg: `${PREFIX}-margin_top_lg`,
  margin_bottom_sm: `${PREFIX}-margin_bottom_sm`,
  margin_bottom_md: `${PREFIX}-margin_bottom_md`,
  margin_bottom_lg: `${PREFIX}-margin_bottom_lg`,
  ol_restart_2: `${PREFIX}-ol_restart_2`,
  ol_restart_3: `${PREFIX}-ol_restart_3`,
  ol_restart_4: `${PREFIX}-ol_restart_4`,
  ol_restart_5: `${PREFIX}-ol_restart_5`,
  ol_restart_6: `${PREFIX}-ol_restart_6`,
};

class MyEditor extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      activeInlineImage: null,
      editorState: this.createEditorState(),
      focused: false,
    };
    this.wrapper = createRef();
  }

  componentDidMount() {
    if (!this.props.readOnly) {
      this.updateExternalToolbarProps();
    }
  }

  componentDidUpdate(prevProps) {
    const { decoratorProps, readOnly, reportLatexErrors, showSolutions, value } = this.props;

    const valueChanged =
      value !== prevProps.value &&
      (!this.propagatedChange || (value !== this.propagatedChange && !isDeepEqual(value, this.propagatedChange))); // JSON.stringify(value) !== JSON.stringify(this.propagatedChange)));
    if (valueChanged) {
      this.setState({ editorState: this.createEditorState() });
    } else if (
      decoratorProps !== prevProps.decoratorProps ||
      readOnly !== prevProps.readOnly ||
      reportLatexErrors !== prevProps.reportLatexErrors ||
      showSolutions !== prevProps.showSolutions
    ) {
      const editorState = this.state.editorState;
      this.setState({
        editorState: EditorState.set(editorState, {
          decorator: this.getDecorator(),
        }),
      });
    }
  }

  createEditorState() {
    const { preview } = this.props;
    let { value } = this.props;
    if (value) {
      if (preview) {
        value = {
          ...value,
          blocks: value.blocks
            .filter(b => b.text.trim() || b.entityRanges?.some(er => value.entityMap[er.key].type === 'EQUATION'))
            .slice(0, preview || 1)
            .map(b => (b.type === 'atomic' ? b : { ...b, type: 'unstyled' })),
        };
      }
      if (value.blocks.length) {
        this.origContentState = convertFromRaw(value);
        return EditorState.createWithContent(this.origContentState, this.getDecorator());
      }
    }
    return EditorState.createEmpty(this.getDecorator());
  }

  getDecorator() {
    const { decoratorProps, onToggleSolutionKey, plugins, readOnly, reportLatexErrors, showSolutions } = this.props;
    return this.buildDecorator(
      plugins,
      decoratorProps,
      readOnly,
      showSolutions,
      onToggleSolutionKey,
      this.pluginFunctions,
      reportLatexErrors,
    );
  }
  buildDecorator = memoize(
    (plugins, decoratorProps, readOnly, showSolutions, onToggleSolutionKey, pluginFunctions, reportLatexErrors) => {
      const decoratorConfigs = Object.keys(plugins || {})
        .map(
          p =>
            plugins[p] &&
            pluginsConfig[p]?.decorator && {
              ...pluginsConfig[p].decorator,
              props: {
                ...decoratorProps,
                readOnly,
                reportLatexErrors,
                ...(readOnly ? { showSolutions, onToggleSolutionKey } : pluginFunctions),
              },
            },
        )
        .filter(Boolean);
      return new CompositeDecorator(decoratorConfigs);
    },
  );

  handleEditorStateChange = (editorState, forcePropagate = false) => {
    const stateUpdate = { editorState };
    if (this.state.focusedBlock) {
      const blockExists = editorState.getCurrentContent().getBlockForKey(this.state.focusedBlock.key);
      // in case selected AtomicBlock was deleted
      if (!blockExists) {
        stateUpdate.focusedBlock = null;
        stateUpdate.toolbarProps = null;
      }
    }
    this.setState(stateUpdate, this.updateExternalToolbarProps);

    // when we just update entity data (ie contentState.replaceEntityData), this doesn't actually change the contentState,
    // which means the changes wont be propagated.  So we clear origContentState to force update propagation.
    if (forcePropagate) this.origContentState = null;
    this.debouncedOnChange();
  };

  handleContentStateChange = contentState => {
    this.handleEditorStateChange(EditorState.push(this.state.editorState, contentState, 'change-block-data'), true);
    // type EditorChangeType = 'adjust-depth' | 'apply-entity' | 'backspace-character' | 'change-block-data' | 'change-block-type' | 'change-inline-style' | 'move-block' | 'delete-character' | 'insert-characters' | 'insert-fragment' | 'redo' | 'remove-range' | 'spellcheck-change' | 'split-block' | 'undo';
  };

  debouncedOnChange = debounce(() => {
    if (this.props.onChange) {
      const { editorState } = this.state;
      const contentState = editorState.getCurrentContent();
      if (contentState !== this.origContentState) {
        this.origContentState = contentState;
        this.propagatedChange = convertToRaw(contentState);
        this.props.onChange(this.propagatedChange);
      }
    }
  }, this.props.debounce || 250);

  setEditorRef = el => {
    const { forwardedRef } = this.props;
    this.editor = el;
    assignForwardedRef(forwardedRef, this);
  };

  focus = () => {
    this.editor.focus();
    this.setState({ focused: true });
  };

  // Draft only clears the selection's `hasFocus` flag on a real DOM blur event, which never fires
  // when a sibling editor takes the caret programmatically (as the table cells do). While the flag
  // is set, this editor re-asserts the DOM selection on its next render and yanks the caret back out
  // of whichever editor now owns it. Uses the updater form so it composes with the decorator refresh
  // that componentDidUpdate queues in the same commit.
  releaseFocus = () => {
    this.setState(({ editorState }) => {
      const selection = editorState.getSelection();
      if (!selection.getHasFocus()) return null;
      return {
        editorState: EditorState.acceptSelection(editorState, selection.merge({ hasFocus: false })),
        focused: false,
      };
    });
  };

  handleFocus = e => {
    this.setState({ focused: true });
    this.props.onFocus?.(e);
  };
  handleBlur = e => {
    this.setState({ focused: false });
    this.props.onBlur?.(e);
  };

  handleWrapperClick = e => {
    if (!this.state.focused) this.focus();
    if (e.target === this.wrapper.current) {
      this.handleEditorStateChange(EditorState.moveFocusToEnd(this.state.editorState));
    }
  };

  // Using similar technique to https://github.com/draft-js-plugins/draft-js-plugins/blob/master/HOW_TO_CREATE_A_PLUGIN.md
  pluginFunctions = {
    getEditorState: () => this.state.editorState,
    setEditorState: this.handleEditorStateChange,
    getReadOnly: () => this.props.readOnly || this.state.forceReadOnly,
    setReadOnly: forceReadOnly => this.setState({ forceReadOnly }),
    setActiveInlineImage: activeInlineImage => this.setState({ activeInlineImage }),
    toggleNestedEditorFocus: focus => this.props.onToggleNestedEditorFocus?.(focus),
    onResetFocus: this.focus,
  };

  keyBindingFn = e => {
    if (e.keyCode === 67 && KeyBindingUtil.hasCommandModifier(e) && e.shiftKey) {
      return 'copy-selection';
      // cmd/ctrl + shift + C
    }
    if (e.keyCode === 86 && KeyBindingUtil.hasCommandModifier(e) && e.shiftKey) {
      return 'paste-selection';
      // cmd/ctrl + shift + V
    }
    if (e.key === 'Backspace' && KeyBindingUtil.hasCommandModifier(e) && e.shiftKey) {
      return 'delete-line';
    }
    if (e.keyCode === 189 && e.altKey) {
      return 'insert-block-equation';
      // opt/alt + '-'
    }

    if (e.keyCode === 187 && e.altKey) {
      return 'insert-inline-equation';
      // opt/alt + '='
    }

    if (e.keyCode === 9) {
      this.handleEditorStateChange(RichUtils.onTab(e, this.state.editorState, 4 /* maxDepth = 4 */));
      // tab
      return;
    }

    if (KeyBindingUtil.hasCommandModifier(e)) {
      const color = TEXT_COLORS.find(({ hotkey }) => hotkey === e.key)?.style;
      if (color) {
        e.preventDefault();
        this.handleEditorStateChange(toggleInlineStyle(this.state.editorState, color));
        return;
      }
    }

    return getDefaultKeyBinding(e);
  };

  handleKeyCommand = command => {
    const { editorState } = this.state;
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      this.handleEditorStateChange(newState);
      return 'handled';
    }

    if (command === 'delete-line') {
      // const { editorState } = this.state;
      const selection = editorState.getSelection();
      // if (selection.isCollapsed() && selection.getFocusOffset() === 0) {
      const contentState = editorState.getCurrentContent();
      // const block = contentState.getBlockForKey(selection.getFocusKey());
      // const prevBlock = contentState.getBlockBefore(selection.getFocusKey());
      // if (block.getType() !== 'atomic' && block.getLength() === 0 && prevBlock?.getType() === 'atomic') {
      const focusKey = selection.getFocusKey();
      const newBlockMap = contentState.getBlockMap().delete(focusKey);
      // Draft cannot render a document with no blocks, so deleting the only one leaves a blank block.
      // Otherwise the caret moves to a neighbour: a selection on the deleted block breaks the next edit.
      const newContentState =
        newBlockMap.size ?
          contentState.merge({
            blockMap: newBlockMap,
            selectionAfter: SelectionState.createEmpty(
              contentState.getKeyAfter(focusKey) ?? contentState.getKeyBefore(focusKey),
            ),
          })
        : EditorState.createEmpty().getCurrentContent();
      const newEditorState = EditorState.push(editorState, newContentState, 'delete-line');
      this.handleEditorStateChange(newEditorState);
      return 'handled';
      //   }
      // }
    }

    if (command === 'copy-selection') {
      // this.copySelection();
      copySelection(editorState, this.handleEditorStateChange);
      return 'handled';
    }
    if (command === 'paste-selection') {
      pasteContent(editorState, this.handleEditorStateChange);
      return 'handled';
    }

    const { plugins } = this.props;

    if (plugins?.equation) {
      if (command === 'insert-block-equation') {
        addEquation(editorState, true, this.handleEditorStateChange);
        return 'handled';
      }

      if (command === 'insert-inline-equation') {
        addEquation(editorState, false, this.handleEditorStateChange);
        return 'handled';
      }
    }

    return 'not-handled';
  };

  handleReturn = (event, editorState) => {
    let newEditorState;
    if (KeyBindingUtil.isSoftNewlineEvent(event)) {
      newEditorState = RichUtils.insertSoftNewline(editorState);
    } else {
      const contentState = editorState.getCurrentContent();
      const selection = editorState.getSelection();
      let newContentState = Modifier.splitBlock(contentState, selection);
      const selAfter = newContentState.getSelectionAfter();
      const origBlock = newContentState.getBlockForKey(selection.getFocusKey());
      // if origBlock was atomic and no entities, change to normal unstyled block
      const origBlockType = origBlock?.getType();
      if (origBlockType === 'atomic' && !origBlock.getCharacterList().some(c => c.getEntity())) {
        newContentState = Modifier.setBlockType(
          newContentState,
          SelectionState.createEmpty(origBlock.getKey()),
          'unstyled',
        );
      }
      const newBlock = origBlock && newContentState.getBlockAfter(origBlock.getKey());
      // if newBlock was atomic and no entities, change to normal unstyled block
      if (newBlock?.getType() === 'atomic' && !newBlock.getCharacterList().some(c => c.getEntity())) {
        newContentState = Modifier.setBlockType(
          newContentState,
          SelectionState.createEmpty(newBlock.getKey()),
          'unstyled',
        );
      }
      newContentState = newContentState.set('selectionAfter', selAfter);
      newEditorState = EditorState.push(editorState, newContentState, 'split-block');

      // if was header, remove block and inline styling
      if (origBlockType?.startsWith('header')) {
        newEditorState = EditorState.set(newEditorState, {
          currentContent: Modifier.setBlockData(newEditorState.getCurrentContent(), newEditorState.getSelection(), {}),
        });
        newEditorState = RichUtils.toggleBlockType(newEditorState, 'unstyled');
        newEditorState = EditorState.setInlineStyleOverride(newEditorState, OrderedSet());
      } else {
        // if not header, copy all block data to current block (excluding certain props)
        newEditorState = EditorState.set(newEditorState, {
          currentContent: Modifier.setBlockData(
            newEditorState.getCurrentContent(),
            newEditorState.getSelection(),
            (origBlock || contentState.getBlockForKey(selection.getFocusKey()))
              .getData()
              .filterNot((v, k) => ['float', 'marginBottom', 'marginTop', 'olRestart'].includes(k)),
          ),
        });
      }
    }

    this.handleEditorStateChange(newEditorState);
    return 'handled';
  };

  blockStyleFn = block => {
    const { canToggleSolutions, readOnly, showSolutions } = this.props;
    const { editorState } = this.state;
    const className = [];
    const contentState = editorState.getCurrentContent();
    const data = block.getData();
    const alignment = data.get('alignment');
    const float = data.get('float');
    const lineSpacing = data.get('lineSpacing');
    const marginTop = data.get('marginTop');
    const marginBottom = data.get('marginBottom');
    const olRestart = data.get('olRestart');
    if (canToggleSolutions && data.get('solution')) {
      className.push(classes.solutionBlock);
      if (readOnly && showSolutions !== true) {
        className.push(classes.solutionBlockHidden);
      }
      const prevBlock = contentState.getBlockBefore(block.getKey());
      const nextBlock = contentState.getBlockAfter(block.getKey());
      const prevSolution = prevBlock && prevBlock.getData().get('solution');
      const nextSolution = nextBlock && nextBlock.getData().get('solution');
      if (!prevSolution) className.push(classes.solutionBlockFirst);
      if (!nextSolution) className.push(classes.solutionBlockLast);
    }
    return classNames(className, {
      [classes[`align${capitalize(alignment || '')}`]]: !!alignment,
      [classes.float]: !!float,
      [classes[`float${capitalize(float || '')}`]]: !!float,
      [classes[`line_spacing_${lineSpacing}`.replace('.', '_')]]: !!lineSpacing,
      [classes[`margin_top_${marginTop}`]]: !!marginTop,
      [classes[`margin_bottom_${marginBottom}`]]: !!marginBottom,
      [classes[`ol_restart_${olRestart}`]]: !!olRestart,
    });
  };

  blockRenderer = block => {
    const blockType = block.getType();
    if (blockType === 'atomic') {
      const { customBlockProps, onToggleSolutionKey, readOnly, reportLatexErrors, showSolutions } = this.props;
      const { editorState, focusedBlock } = this.state;

      const contentState = editorState.getCurrentContent();
      const entityKey = block.getEntityAt(0);
      const entity = entityKey && contentState.getEntity(entityKey);

      if (entity) {
        const entityConfig = BLOCK_RENDER_MAP[entity.getType()];

        if (entityConfig) {
          return {
            ...entityConfig,
            props: {
              readOnly,
              onUpdateContentState: this.handleContentStateChange,
              onFocus: this.focusContentBlock,
              onBlur: this.blurContentBlock,
              onResetFocus: this.focus,
              focusedBlockKey: focusedBlock?.key,
              onUpdateToolbarProps: this.updateToolbarProps,
              showSolutions: showSolutions,
              onToggleSolutionKey,
              reportLatexErrors,
              ...customBlockProps,
            },
          };
        }
      }
    }

    return null;
  };

  focusContentBlock = (blockType, blockKey, toolbarProps) => {
    // console.log('Handle Focus Content Block');
    const editorState = this.state.editorState;
    this.setState(
      {
        focused: true,
        focusedBlock: { type: blockType, key: blockKey },
        editorState: EditorState.forceSelection(editorState, SelectionState.createEmpty(blockKey)),
        toolbarProps,
      },
      this.updateExternalToolbarProps,
    );
    this.props.onToggleNestedEditorFocus && this.props.onToggleNestedEditorFocus(true);
  };
  blurContentBlock = () => {
    // console.log('Handle Blur Content Block');
    const editorState = this.state.editorState;
    this.setState(
      {
        focused: false,
        focusedBlock: null,
        editorState: EditorState.forceSelection(editorState, editorState.getSelection()),
        toolbarProps: null,
      },
      this.updateExternalToolbarProps,
    );
    this.props.onToggleNestedEditorFocus && this.props.onToggleNestedEditorFocus(false);
  };

  updateToolbarProps = toolbarProps => this.setState({ toolbarProps });

  updateExternalToolbarProps = () => {
    const { onToggleNestedEditorFocus, onUpdateToolbarProps } = this.props;
    const { editorState, focusedBlock } = this.state;
    if (onUpdateToolbarProps) {
      onUpdateToolbarProps({
        editorState,
        onChange: this.handleEditorStateChange,
        onFocus: this.focus,
        focusedBlock,
        onToggleNestedEditorFocus,
      });
    }
  };

  render() {
    const {
      className,
      continuousLists,
      fitContent,
      forwardedRef,
      onChange,
      onMouseDown,
      onUpdateToolbarProps,
      plugins,
      preview,
      readOnly,
      reportLatexErrors,
      theme,
      value,
      ...rest
    } = this.props;
    const { editorState, focusedBlock, forceReadOnly } = this.state;

    return (
      <div
        className={classNames(classes.wrapper, className, {
          [classes.wrapper_continuousLists]: continuousLists,
          [classes.wrapper_fitContent]: fitContent,
          [classes.wrapper_readOnly]: readOnly,
          [classes.wrapper_hidePlaceholder]:
            !readOnly &&
            !editorState.getCurrentContent().hasText() &&
            editorState.getCurrentContent().getBlockMap().first().getType() !== 'unstyled',
        })}
        onClick={readOnly ? undefined : this.handleWrapperClick}
        onMouseDown={onMouseDown}
        ref={this.wrapper}
        style={preview ? { maxHeight: `${preview * 1.43}em` } : undefined}
      >
        {!(readOnly || onUpdateToolbarProps) && this.renderToolbar()}
        <Editor
          blockRendererFn={this.blockRenderer}
          blockRenderMap={extendedBlockRenderMap}
          blockStyleFn={this.blockStyleFn}
          customStyleMap={defaultStyleMap}
          editorState={editorState}
          handleDroppedFiles={this.handleDroppedFiles}
          handleKeyCommand={this.handleKeyCommand}
          handlePastedFiles={this.handlePastedFiles}
          handleReturn={this.handleReturn}
          keyBindingFn={this.keyBindingFn}
          onBlur={this.handleBlur}
          onChange={this.handleEditorStateChange}
          onFocus={this.handleFocus}
          readOnly={Boolean(readOnly || forceReadOnly || focusedBlock)}
          ref={this.setEditorRef}
          spellCheck
          {...rest}
        />
        {!readOnly && this.renderFileUploader()}
      </div>
    );
  }

  renderToolbar() {
    const { plugins } = this.props;
    const { activeInlineImage, editorState, focused, focusedBlock, forceReadOnly, toolbarProps } = this.state;

    return (
      <Toolbar
        activeInlineImage={activeInlineImage}
        anchorEl={this.wrapper.current}
        editorState={editorState}
        focusedBlock={focusedBlock}
        nested={Boolean(toolbarProps)}
        onChange={this.handleEditorStateChange}
        onFocus={this.focus}
        open={Boolean(focused || focusedBlock || forceReadOnly)}
        plugins={plugins}
        {...toolbarProps}
      />
    );
  }

  handleDroppedFiles = (_, files) => {
    this.setState({ droppedFiles: files.filter(f => f.type.match(/image/)) });
    return 'handled';
  };
  handlePastedFiles = files => {
    this.setState({ droppedFiles: files.filter(f => f.type.match(/image/)) });
    return 'handled';
  };

  renderFileUploader() {
    const { droppedFiles } = this.state;
    return <AttachmentUploadManager files={droppedFiles} onUpload={this.insertImageinEditor} />;
  }

  insertImageinEditor = attachment => {
    const { editorState } = this.state;
    const selectionState = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('IMAGE', 'IMMUTABLE', {
      url: attachment.urls.original,
      id: attachment.id,
    });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    let newEditorState = EditorState.set(editorState, {
      currentContent: contentStateWithEntity,
      selection: selectionState,
    });
    newEditorState = AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, ' ');
    this.handleEditorStateChange(newEditorState);
  };
}

// Draft reads the entry as a plain object (`config.element`), but Immutable deep-converts a plain
// object passed to `merge` — which would turn the override into a Map whose `element` Draft cannot
// see. Wrapping it in a `Map` keeps the value as the plain object Draft expects.
const extendedBlockRenderMap = DefaultDraftBlockRenderMap.merge(Map({ atomic: { element: 'div' } }));

const BLOCK_RENDER_MAP = {
  IMAGE: {
    component: ImageBlock,
    editable: false,
  },
  EQUATION: {
    component: EquationBlock,
    editable: false,
  },
  VIDEO: {
    component: VideoBlock,
    editable: false,
  },
  PAGE_BREAK: {
    component: PageBreakBlock,
    editable: false,
  },
  CALLOUT: {
    component: Callout,
    editable: false,
  },
  TABLE: {
    component: TableBlock,
    editable: false,
  },
};

const olRestartStyles = [...Array(5)].reduce((mem, _, idx) => {
  mem[`& .${classes[`ol_restart_${idx + 2}`]}`] = [...Array(5)].reduce((mem2, _, idx2) => {
    mem2[`&.public-DraftStyleDefault-depth${idx2}`] = {
      counterReset: `ol${idx2} ${idx + 1} !important`,
    };
    return mem2;
  }, {});
  return mem;
}, {});
const StyledMyEditorWithStyles = styled(MyEditor)(({ theme: { breakpoints, palette, spacing } }) => ({
  [`&.${classes.wrapper}`]: {
    containerType: 'inline-size', // breaks usages with no extrinsic/context width (chat MessageItem) — pass `fitContent` there
    position: 'relative',
    overflow: 'hidden', // required to prevent floating image overflow
    // setting only overflowX as hidden automatically computes overflowY to auto

    [`&.${classes.wrapper_fitContent}`]: { containerType: 'normal' },
    '&:focus': { outline: 'none' },
    [`&:not(.${classes.wrapper_readOnly})`]: { cursor: 'text' },

    // List Item Styling
    '& .public-DraftStyleDefault-unorderedListItem, .public-DraftStyleDefault-orderedListItem': {
      display: 'flex',
    },
    '& .public-DraftStyleDefault-unorderedListItem:before': {
      // ∙  •  ・  ◦  ●  ○  ■  □  ➢  ➣  ➤
      marginRight: 8,
      flexShrink: 0,
    },
    '& .public-DraftStyleDefault-unorderedListItem.public-DraftStyleDefault-depth0:before': {
      content: '"●"',
    },
    '& .public-DraftStyleDefault-unorderedListItem.public-DraftStyleDefault-depth1:before': {
      content: '"○"',
    },
    '& .public-DraftStyleDefault-unorderedListItem.public-DraftStyleDefault-depth2:before': {
      content: '"■"',
    },
    '& .public-DraftStyleDefault-unorderedListItem.public-DraftStyleDefault-depth3:before': {
      content: '"□"',
    },
    '& .public-DraftStyleDefault-unorderedListItem.public-DraftStyleDefault-depth4:before': {
      content: '"➢"',
    },
    '& .public-DraftStyleDefault-orderedListItem.public-DraftStyleDefault-listLTR:before': {
      position: 'static',
      left: 'initial',
      marginLeft: -16,
      marginRight: 8,
      flexShrink: 0,
    },
  },

  [`&.${classes.wrapper_hidePlaceholder}`]: {
    '& .public-DraftEditorPlaceholder-root': { display: 'none' },
  },

  // Draft ends a list wherever the marker changes, so markdown's "numbered item with bullets nested
  // under it" arrives as an <ol>, a <ul> and another <ol> — three lists in a row, the numbered ones
  // each counting from 1 and each spaced off the last like a new paragraph. Turn this on for content
  // converted from markdown, where two lists sitting directly against each other are one list: they
  // are joined back up with tight spacing and a single counter. An authored document leaves it off,
  // since there a `<ul>` following an `<ol>` really is a second list.
  //
  // The top-level count has to be owned by the wrapper rather than by Draft's own restart on the
  // first item of each run, because a counter's scope reaches an element's following siblings but not
  // its parent's — so a count kept on an <li> is invisible to the next <ol>, which is why the
  // numbering restarts without this. Hoisting it one level up puts the next wrapper in that scope.
  // Nested levels are left to Draft: they count in their own counters, and a sub-list cut short by a
  // marker change is meant to start again anyway.
  [`&.${classes.wrapper_continuousLists}`]: {
    '& [data-contents] > :is(ol, ul)': { counterReset: 'ol0' },
    '& [data-contents] > :is(ol, ul) > .public-DraftStyleDefault-depth0.public-DraftStyleDefault-reset:first-child': {
      counterReset: 'none',
    },
    // Both sides of the seam, since Draft gives every list a 16px margin and the larger of the two
    // is what survives the collapse.
    '& [data-contents] > :is(ol, ul):has(+ :is(ol, ul))': { marginBottom: 0 },
    '& [data-contents] > :is(ol, ul) + :is(ol, ul)': { counterReset: 'none', marginTop: spacing(0.5) },
  },

  // Alignment Block Styling
  [`& .${classes.alignLeft}`]: {
    textAlign: 'left',
    '& > .public-DraftStyleDefault-block': { textAlign: 'left' },
    '&.public-DraftStyleDefault-unorderedListItem, &.public-DraftStyleDefault-orderedListItem': {
      justifyContent: 'flex-start',
    },
  },

  [`& .${classes.alignCenter}`]: {
    textAlign: 'center',
    '& > .public-DraftStyleDefault-block': { textAlign: 'center' },
    '&.public-DraftStyleDefault-unorderedListItem, &.public-DraftStyleDefault-orderedListItem': {
      justifyContent: 'center',
    },
  },

  [`& .${classes.alignRight}`]: {
    textAlign: 'right',
    '& > .public-DraftStyleDefault-block': { textAlign: 'right' },
    '&.public-DraftStyleDefault-unorderedListItem, &.public-DraftStyleDefault-orderedListItem': {
      justifyContent: 'flex-end',
    },
  },

  [`& .${classes.alignJustify}`]: {
    textAlign: 'justify',
    '& > .public-DraftStyleDefault-block': { textAlign: 'justify' },
    '&.public-DraftStyleDefault-unorderedListItem, &.public-DraftStyleDefault-orderedListItem': {
      justifyContent: 'stretch',
    },
  },

  // Float Block Styling
  [`& .${classes.float}`]: {
    position: 'relative',
    zIndex: 1,
    margin: spacing(1, 0),
  },

  [`& .${classes.floatRight}`]: {
    float: 'right',
    marginLeft: spacing(3),
  },

  [`& .${classes.floatLeft}`]: {
    float: 'left',
    marginRight: spacing(3),
  },

  [breakpoints.only('xs')]: {
    [`& .${classes.floatRight}`]: { marginLeft: 0 },
    [`& .${classes.floatLeft}`]: { marginRight: 0 },
    [`& .${classes.float}`]: { float: 'none', textAlign: 'center' },
  },
  [`@container (max-width: ${breakpoints.values.sm}px)`]: {
    [`& .${classes.floatRight}`]: { marginLeft: 0 },
    [`& .${classes.floatLeft}`]: { marginRight: 0 },
    [`& .${classes.float}`]: { float: 'none', textAlign: 'center' },
  },

  // placed before margin top/bottom styling, so it doesn't overwrite padding/margin
  [`& .${classes.solutionBlock}`]: {
    backgroundColor: palette.primary.background,
    padding: spacing(0, 1),
    margin: 0,
    // boxShadow: shadows[2],
    [`&.${classes.solutionBlockFirst}`]: {
      borderTopLeftRadius: spacing(1),
      borderTopRightRadius: spacing(1),
      paddingTop: spacing(1),
    },
    [`&.${classes.solutionBlockLast}`]: {
      borderBottomLeftRadius: spacing(1),
      borderBottomRightRadius: spacing(1),
      paddingBottom: spacing(1),
    },
  },

  [`&.${classes.wrapper_readOnly} .${classes.solutionBlockHidden}`]: { opacity: 0 },

  // Line-spacing Block Styling
  [`& .${classes.line_spacing_1}`]: { lineHeight: '1' },

  [`& .${classes.line_spacing_1_5}`]: { lineHeight: '1.5' },
  [`& .${classes.line_spacing_2}`]: { lineHeight: '2' },
  [`& .${classes.line_spacing_3}`]: { lineHeight: '3' },

  // Margin Top/Bottom Styling
  [`& .${classes.margin_top_sm}`]: { paddingTop: spacing(2) },
  [`& .${classes.margin_top_md}`]: { paddingTop: spacing(4) },
  [`& .${classes.margin_top_lg}`]: { paddingTop: spacing(6) },
  [`& .${classes.margin_bottom_sm}`]: { paddingBottom: spacing(2) },
  [`& .${classes.margin_bottom_md}`]: { paddingBottom: spacing(4) },
  [`& .${classes.margin_bottom_lg}`]: { paddingBottom: spacing(6) },

  // Ordered List Restart
  ...olRestartStyles,
}));

export default forwardRef(function Editor(props, ref) {
  return <StyledMyEditorWithStyles {...props} forwardedRef={ref} />;
});
